import { useRef } from "react";
import { useParams } from "react-router-dom";
import { Box } from "@mui/material";
import {
  ActionButton,
  Button,
  Container,
  Icon,
  Notifications,
} from "@jds/core";

import sampleExcel from "../../assets/png/SampleExcel.png";
import ExcelFile from "../../assets/excel/Sample_Excel.xlsx";

import useApiHttp from "../../hooks/use-http";
import classes from "./BulkGuestInvite.module.css";
import {
  downloadFile,
  excelValidationCheck,
  validateFileSignature,
} from "../../util/helper";

const INVALID_FILE_MESSAGE = "Please upload .xlsx format file";

const BulkGuestInvite = ({ role = "GUEST", onSuccess }) => {
  const bulkUploadRef = useRef(null);
  const { id: eventId } = useParams();

  const { sendRequest: bulkUpload, isLoading: bulkUploadLoading } =
    useApiHttp();

  const showInvalidFileToast = () => {
    Notifications.toast.error({
      id: "user-bulk",
      title: INVALID_FILE_MESSAGE,
    });
  };

  const handleBulkUpload = (file) => {
    const formData = new FormData();

    formData.append("eventId", eventId);
    formData.append("file", file);
    formData.append("role", role);

    bulkUpload(
      {
        url: "event-attendee/upload",
        method: "POST",
        body: formData,
        formData: true,
      },
      null,
      (data) => {
        Notifications.toast.success({
          id: "bulk-add-user",
          title: data?.message,
        });

        onSuccess?.();
      },
    );
  };

  const validateAndUpload = async (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    try {
      if (!excelValidationCheck(event)) {
        return showInvalidFileToast();
      }

      const isValid = await validateFileSignature(file, {
        xlsx: ["504b0304"],
      });

      if (!isValid) {
        return showInvalidFileToast();
      }

      handleBulkUpload(file);
    } catch {
      showInvalidFileToast();
    } finally {
      bulkUploadRef.current.value = null;
    }
  };

  return (
    <div className={classes.container}>
      <input
        ref={bulkUploadRef}
        type="file"
        accept=".xlsx"
        hidden
        onChange={validateAndUpload}
      />

      <h3 className={classes.heading}>XLSX File Upload Format</h3>

      <img
        src={sampleExcel}
        alt="Sample Excel"
        draggable={false}
        className={classes.previewImage}
      />

      <p className={classes.description}>
        Please upload the XLSX file in the given format only.
      </p>

      <div className={classes.infoCard}>
        <div className={classes.info}>
          <p className={classes.infoTitle}>Need a starting point?</p>
          <p className={classes.infoDescription}>
            Download the sample XLSX with the right headers.
          </p>
        </div>
        <div className={classes.downloadBtn}>
          <span onClick={() => downloadFile(ExcelFile, "Sample_Excel")}>
            {" "}
            Sample XLSX
          </span>
          <Box>
            <Icon ic="IcFileRequest" color="primary" />
          </Box>
        </div>
      </div>

      <Container fullWidth>
        <ActionButton
          label="Bulk Upload"
          kind="secondary"
          state={bulkUploadLoading ? "loading" : "default"}
          onClick={() => bulkUploadRef.current?.click()}
          stretch
          size={"large"}
        />
      </Container>
    </div>
  );
};

export default BulkGuestInvite;
